import { PartRelationshipNode, PartRelationshipNodeType } from '@/api/generatedApi';
import { usePartFinderSearch } from '@/components/PartFinder/usePartFinderSearch';
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard';
import { useToast } from '@/hooks/useToast';
import { useTenants } from '@/services/tenants/useTenants';
import {
  CenterFocusStrongOutlined,
  ContentCopyOutlined,
  DescriptionOutlined,
  ManageSearchOutlined,
  OpenInNewOutlined,
  StorefrontOutlined,
  ZoomOutMapOutlined,
} from '@mui/icons-material';
import { ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { NodeActionKey, nodeActions } from './relationshipNodeActions';

export interface NodeMenuAnchor {
  node: PartRelationshipNode;
  top: number;
  left: number;
}

const ACTION_ICONS: Record<NodeActionKey, ReactNode> = {
  'open-report': <DescriptionOutlined fontSize='small' />,
  'run-report': <ManageSearchOutlined fontSize='small' />,
  'show-supplier': <StorefrontOutlined fontSize='small' />,
  'open-website': <OpenInNewOutlined fontSize='small' />,
  copy: <ContentCopyOutlined fontSize='small' />,
  focus: <CenterFocusStrongOutlined fontSize='small' />,
  'show-all': <ZoomOutMapOutlined fontSize='small' />,
};

const niinOfNsnNode = (nodeId: string): string => nodeId.replace(/^nsn:/, '');

const useNodeActionHandlers = (seedNodeId: string, onToggleFocus: (nodeId: string) => void) => {
  const { runSearch } = usePartFinderSearch();
  const { showToast } = useToast();
  const { copy } = useCopyToClipboard();
  const { getPath } = useTenants();
  const navigate = useNavigate();

  const runReport = async (node: PartRelationshipNode) => {
    const niin = node.type === PartRelationshipNodeType.PartNumber ? niinOfNsnNode(seedNodeId) : undefined;
    try {
      await runSearch({ partNumber: node.label, niin });
    } catch {
      showToast({ message: 'Could not start that report. Please try again.', type: 'error' });
    }
  };

  return (key: NodeActionKey, node: PartRelationshipNode) => {
    const handlers: Record<NodeActionKey, () => void> = {
      'open-report': () => {
        navigate(getPath(`part-finder-reports/${node.reportId}`));
      },
      'run-report': () => void runReport(node),
      'show-supplier': () => {
        const row = document.querySelector(`[id="sourcing-opportunity-${node.sourcingOpportunityId}"]`);
        if (row) row.scrollIntoView({ behavior: 'smooth', block: 'center' });
        else showToast({ message: `Expand Sourcing Opportunities to see ${node.label}.`, type: 'info' });
      },
      'open-website': () => window.open(node.url, '_blank', 'noopener,noreferrer'),
      copy: () => void copy(node.label),
      focus: () => onToggleFocus(node.id),
      'show-all': () => onToggleFocus(node.id),
    };
    handlers[key]();
  };
};

export const RelationshipNodeMenu = ({
  anchor,
  seedNodeId,
  focusedNodeId,
  onClose,
  onToggleFocus,
}: {
  anchor?: NodeMenuAnchor;
  seedNodeId: string;
  focusedNodeId?: string;
  onClose: () => void;
  onToggleFocus: (nodeId: string) => void;
}) => {
  const runAction = useNodeActionHandlers(seedNodeId, onToggleFocus);
  const actions = anchor
    ? nodeActions(anchor.node, anchor.node.id === seedNodeId, anchor.node.id === focusedNodeId)
    : [];
  return (
    <Menu
      open={!!anchor}
      onClose={onClose}
      anchorReference='anchorPosition'
      disableRestoreFocus
      disableScrollLock
      anchorPosition={anchor && { top: anchor.top, left: anchor.left }}
    >
      {actions.map(action => (
        <MenuItem
          key={action.key}
          onClick={() => {
            onClose();
            if (anchor) runAction(action.key, anchor.node);
          }}
        >
          <ListItemIcon>{ACTION_ICONS[action.key]}</ListItemIcon>
          <ListItemText>{action.label}</ListItemText>
        </MenuItem>
      ))}
    </Menu>
  );
};
